import { useEffect, useMemo, useState } from 'react';
import type { SpeciesId } from '../../types.ts';
import { useIsMobile } from '../../util/useIsMobile.ts';
import { Sprite } from '../Sprite.tsx';
import { TypeChip } from '../TypeChip.tsx';
import { type BuilderSpecies, getSpeciesCatalog } from './builder-data.ts';

type SortMode = 'usage' | 'name';

// Modal overlay for choosing a species — used both for empty-slot
// instantiation and species swaps on an existing slot. Backdrop click and
// Escape close it. Renders the full catalog with a fuzzy filter.
//
// Type filter is intentionally NOT yet implemented (ruthless-pm cut from v1).
// Search-by-name is the primary path; the chip-filter row from the design
// spec was deferred.

export function SpeciesPicker({
  open,
  onClose,
  onPick,
  currentSpecies,
  regulationId = 'M-A',
  includeMegas = false,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (species: BuilderSpecies) => void;
  currentSpecies?: string;
  regulationId?: string;
  // When true, the catalog includes Pikalytics mega forms as separate
  // pickable entries (Aerodactyl-Mega, Charizard-Mega-Y, etc.). Use for
  // rail defenders/speed-extras; leave false for team-slot picks (where
  // the mega form is conveyed via the held item).
  includeMegas?: boolean;
}) {
  const [catalog, setCatalog] = useState<BuilderSpecies[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortMode>('usage');
  const isMobile = useIsMobile();

  // Load once when first opened. Cached at the module level so subsequent
  // opens are instant.
  useEffect(() => {
    if (!open || catalog) return;
    let cancelled = false;
    getSpeciesCatalog(regulationId, includeMegas)
      .then((c) => !cancelled && setCatalog(c))
      .catch((e) => !cancelled && setErr(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, [open, catalog, regulationId, includeMegas]);

  // Reset search on open so re-opening doesn't carry over the previous query.
  useEffect(() => {
    if (open) setQuery('');
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!catalog) return [];
    const q = query.trim().toLowerCase();
    const list = !q
      ? catalog
      : catalog.filter((s) => {
          const hay = `${s.name} ${s.types.join(' ')}`.toLowerCase();
          return hay.includes(q);
        });
    // Re-sort according to the active mode (catalog is server-sorted by
    // usage, but the user can flip to alphabetical).
    if (sort === 'name') {
      return [...list].sort((a, b) => a.name.localeCompare(b.name));
    }
    return list;
  }, [catalog, query, sort]);

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Enter' && onClose()}
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        zIndex: 100,
        display: 'flex',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'center',
        padding: isMobile ? 0 : 24,
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          // On mobile we go effectively full-screen — gives the species list
          // every horizontal pixel and stops type chips from spilling off
          // the right edge.
          width: isMobile ? '100vw' : 'min(720px, 95vw)',
          maxWidth: '100vw',
          height: isMobile ? '100dvh' : undefined,
          maxHeight: isMobile ? '100dvh' : '85vh',
          background: 'var(--bg-1)',
          border: isMobile ? 'none' : '1px solid var(--line-2)',
          borderRadius: isMobile ? 0 : 6,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '12px 16px',
            borderBottom: '1px solid var(--line-1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div
              className="caps mono"
              style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
            >
              {currentSpecies ? 'swap species' : 'pick a pokémon'}
            </div>
            {currentSpecies && (
              <div style={{ fontSize: 13, color: 'var(--fg-1)', marginTop: 2 }}>
                currently <strong>{currentSpecies}</strong>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="caps mono"
            style={{
              fontSize: 11,
              color: 'var(--fg-3)',
              background: 'var(--bg-2)',
              border: '1px solid var(--line-1)',
              borderRadius: 2,
              padding: '3px 8px',
              cursor: 'pointer',
            }}
          >
            close
          </button>
        </div>
        <input
          type="text"
          // biome-ignore lint/a11y/noAutofocus: modal-on-open is the appropriate place for autofocus
          autoFocus
          placeholder="search by name or type…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="mono"
          style={{
            padding: '10px 16px',
            // iOS Safari auto-zooms any input with font-size < 16px on focus,
            // which throws away the careful mobile sizing. Bump to 16 on
            // mobile to suppress the zoom; desktop stays at 13.
            fontSize: isMobile ? 16 : 13,
            color: 'var(--fg-1)',
            background: 'var(--bg-2)',
            border: 'none',
            borderBottom: '1px solid var(--line-1)',
            outline: 'none',
          }}
        />
        {/* Sort toggle — server returns catalog usage-sorted; this lets users
            flip to alphabetical. Pinned right under the search so it's
            visible without scrolling on mobile. */}
        <div
          style={{
            display: 'flex',
            gap: 6,
            padding: '6px 10px',
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--line-1)',
            alignItems: 'center',
          }}
        >
          <span
            className="caps mono"
            style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em', marginRight: 4 }}
          >
            sort
          </span>
          <SortChip active={sort === 'usage'} onClick={() => setSort('usage')}>
            usage
          </SortChip>
          <SortChip active={sort === 'name'} onClick={() => setSort('name')}>
            A–Z
          </SortChip>
        </div>
        <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {err ? (
            <div style={{ padding: 16, color: 'var(--neg)', fontSize: 12 }}>
              Failed to load species catalog: {err}
            </div>
          ) : !catalog ? (
            <div className="mono" style={{ padding: 16, color: 'var(--fg-4)', fontSize: 12 }}>
              loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="mono" style={{ padding: 16, color: 'var(--fg-4)', fontSize: 12 }}>
              no matches
            </div>
          ) : (
            filtered.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => {
                  onPick(s);
                  onClose();
                }}
                style={{
                  width: '100%',
                  display: 'grid',
                  // Mobile: drop type-chip column, fold types into the name
                  // row as a single muted label so it can wrap. Desktop:
                  // sprite | name+types | usage % on the right.
                  gridTemplateColumns: isMobile ? '36px 1fr auto' : '40px 1fr auto auto',
                  gap: isMobile ? 10 : 12,
                  alignItems: 'center',
                  padding: isMobile ? '7px 12px' : '7px 16px',
                  background: s.id === currentSpecies ? 'var(--accent-bg)' : 'transparent',
                  border: 'none',
                  borderBottom: '1px solid var(--line-1)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: 'var(--fg-1)',
                  minWidth: 0,
                }}
              >
                <Sprite species={s.id as SpeciesId} size={isMobile ? 28 : 32} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {s.name}
                  </span>
                  {isMobile && (
                    <span
                      className="mono"
                      style={{
                        fontSize: 9.5,
                        color: 'var(--fg-4)',
                        letterSpacing: '0.04em',
                      }}
                    >
                      {s.types.join(' · ')}
                    </span>
                  )}
                </div>
                {!isMobile && (
                  <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                    {s.types.map((t) => (
                      <TypeChip key={t} type={t as any} size="sm" />
                    ))}
                  </div>
                )}
                {s.usage_pct > 0 ? (
                  <span
                    className="mono tabular"
                    style={{
                      fontSize: 10,
                      color: 'var(--fg-3)',
                      whiteSpace: 'nowrap',
                    }}
                    title="Pikalytics usage %"
                  >
                    {s.usage_pct.toFixed(1)}%
                  </span>
                ) : (
                  <span style={{ width: 30 }} />
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// Tiny toggle chip for the picker's sort row. Active state uses the accent
// background so the current mode is unambiguous.
function SortChip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="caps mono"
      style={{
        fontSize: 9,
        padding: '3px 9px',
        background: active ? 'var(--accent-bg)' : 'transparent',
        color: active ? 'var(--accent-2)' : 'var(--fg-3)',
        border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line-1)'}`,
        borderRadius: 2,
        letterSpacing: '0.06em',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}
